/**
 * ============================================================================
 * WHERE THE READER CAME FROM, INSIDE THIS DOCUMENT
 * ============================================================================
 *
 * One question, asked by one screen: "is the page behind me in this tab's
 * history the one I want to send this reader back to?" A back affordance that
 * cannot answer it has to choose between two wrong behaviours — `history.back()`
 * that throws somebody out of the app because they arrived on a shared link, or
 * a plain forward link that lands everybody at the top of a feed they had
 * scrolled halfway down (OSO-630).
 *
 * WHAT THIS IS NOT. It is not a router, not a breadcrumb trail and not a
 * general "previous page" service: it remembers the last two paths and how the
 * current one was reached, which is all the question needs. `RouteHistory`
 * (mounted once in the root layout) is the only writer.
 *
 * WHAT WAS TRIED AND REJECTED
 *
 *  - `document.referrer`. It is the referrer of the DOCUMENT, so it does not
 *    move during client-side navigation — every page in a session reports the
 *    same value, usually "" — and it is the wrong answer twice over for a link
 *    opened from another site.
 *  - `sessionStorage`. It is COPIED into a new tab when a link is opened there,
 *    so "I was on the group feed" survives into a tab whose history is one
 *    entry long. That is precisely the false positive that would walk somebody
 *    out of the app, and it is the common case for a shared post.
 *  - `history.length`. It counts entries without naming any of them, and a tab
 *    that has been used at all is already longer than 1.
 *  - Stamping our own field into `history.state`. It would survive a reload and
 *    address the exact entry, which is genuinely better — but that object
 *    belongs to the App Router (it carries the router tree and the scroll
 *    positions this whole feature exists to preserve), and writing into it
 *    behind Next's back is not a bet worth making for one button.
 *
 * NOTHING PERSISTS. A reload, a new tab and a cold open all start empty, and an
 * empty store answers `null` — the safe answer, the plain link. That is a
 * feature: after a reload the module cannot know what is behind the entry, so
 * it does not claim to.
 */

/** The path before `current`, or null when this document has only seen one. */
let previous: string | null = null;
let current: string | null = null;
/**
 * HOW `current` WAS REACHED. A back or forward step lands on an entry whose
 * NEIGHBOUR is where the reader just was, not where they would go by stepping
 * back again: feed -> post -> (back) feed -> (forward) post leaves `previous`
 * saying "feed" while `history.back()` from there leads out of the app. So a
 * popped-to route answers `null` and gets the link.
 */
let currentIsPop = false;
/** A `popstate` has fired and the route change it causes has not landed yet. */
let pendingPop = false;

export function recordRoute(path: string): void {
  if (path === current) return;
  previous = current;
  current = path;
  currentIsPop = pendingPop;
  pendingPop = false;
}

export function markPopNavigation(): void {
  pendingPop = true;
}

/**
 * The path `history.back()` would return to from `path`, or null when this
 * document does not know — the two are deliberately the same answer.
 *
 * ASKED ABOUT A PATH RATHER THAN ABOUT "NOW", so the answer does not depend on
 * whether the writer's effect has run yet. A page renders before the effect
 * that records it, so `path !== current` means "not recorded yet, and `current`
 * is therefore the page before it"; afterwards the same call reads `previous`.
 * Both orders give the same answer, which is the point — the alternative was a
 * consumer that had to know where in the commit it stood.
 */
export function routeBefore(path: string): string | null {
  if (path !== current) return pendingPop ? null : current;
  return currentIsPop ? null : previous;
}

/** Test seam. */
export function __resetRouteHistory(): void {
  previous = null;
  current = null;
  currentIsPop = false;
  pendingPop = false;
}
